import React, { useState } from "react";
import {
  FaIdCard,
  FaEdit,
  FaCheck,
  FaTimes,
  FaUpload,
  FaSpinner,
} from "react-icons/fa";
import API from "../config/config";
import axios from "axios";
import DocumentStatusEditor from "../components/DocumentStatusEditor";
import { DocumentImage } from "./DocumentImageViewer";

const AADHAAR_DUPLICATE_MESSAGE = "aadhaar number is already used";

const isDuplicateAadhaarMessage = (message) => {
  const normalized = String(message || "").toLowerCase();
  return normalized === AADHAAR_DUPLICATE_MESSAGE;
};

const AadhaarDetails = ({ aadhaar, driverId, editable, editableDetails, editableStatus, currentUserRole, showIfEmpty = false, onUpdated }) => {
  const token = localStorage.getItem("adminToken");
  const detailsEditable = editableDetails !== undefined ? editableDetails : editable;
  const statusEditable = editableStatus !== undefined ? editableStatus : editable;

  // ✅ Hooks must come first
  const [editField, setEditField] = useState("");
  const [uploadingSide, setUploadingSide] = useState(null);
  const [statusEditing, setStatusEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [aadhaarNumberError, setAadhaarNumberError] = useState("");

  const [formData, setFormData] = useState({
    fullName: aadhaar?.fullName || "",
    aadhaarNumber: aadhaar?.aadhaarNumber || "",
    status: aadhaar?.status || "",
    statusReason: aadhaar?.statusReason || "",
    aadhaarfront: aadhaar?.aadhaarfront || "",
    aadhaarback: aadhaar?.aadhaarback || "",
  });

  // ✅ Perform conditional logic AFTER hooks
  const noAadhaarData =
    !aadhaar ||
    (!aadhaar.aadhaarNumber &&
      !aadhaar.aadhaarfront &&
      !aadhaar.aadhaarback &&
      !aadhaar.fullName);

  // Show component if showIfEmpty is true, even when empty
  if (!showIfEmpty && noAadhaarData) return null;
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "aadhaarNumber") {
      setAadhaarNumberError("");
    }
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const isAadhaarNumberAlreadyUsed = async (trimmedAadhaarNumber) => {
    const response = await axios.get(API.ADMIN.DUPLICATE_AADHAAR_CHECK, {
      params: {
        aadhaarNumber: trimmedAadhaarNumber,
        excludeDriverId: driverId,
      },
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data?.exists === true;
  };

  const handleFileChange = async (e, side) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingSide(side);

    try {
      const formdata = new FormData();
      formdata.append("documentFor", "Driver");
      formdata.append(
        "type",
        side === "aadhaarfront" ? "aadhaar_front" : "aadhaar_back"
      );
      formdata.append("file", file, file.name);
      formdata.append("ownerId", driverId);

      const response = await fetch(API.ADMIN.ADMINDOCUMENT_UPLOAD, {
        method: "POST",
        headers: {
          Authorization: "Bearer " + token,
          // Do NOT set Content-Type manually
        },
        body: formdata,
      });

      if (!response.ok) {
        throw new Error(`Failed to upload ${side}`);
      }

      const data = await response.json();
      const uploadedUrl = data.document?.documentUrl;

      if (!uploadedUrl) throw new Error("Upload URL not returned.");

      // If aadhaar record doesn't exist, create it using ADMIN_VERIFY_AADHAAR
      if (!aadhaar || !aadhaar._id) {
        const payload = {
          ownerId: driverId,
          aadhaarNumber: formData.aadhaarNumber || "",
          nameToMatch: formData.fullName || "",
          consent: "Y",
          consentText: "I hereby declare my consent agreement for fetching my information via ZOOP API",
          taskId: "08b01aa8-9487-4e6d-a0f0-c796839d6b77",
          aadhaarfront: side === "aadhaarfront" ? uploadedUrl : formData.aadhaarfront || "",
          aadhaarback: side === "aadhaarback" ? uploadedUrl : formData.aadhaarback || "",
        };

        await fetch(API.ADMIN.ADMIN_VERIFY_AADHAAR, {
          method: "POST",
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });

        // Refresh driver data
        if (onUpdated) onUpdated();
      } else {
        // 🔁 Update the existing Aadhaar record in DB
        const patchPayload = {
          [side]: uploadedUrl,
        };

        await axios.patch(
          `${API.ADMIN.AADHAAR_DETAILS}/${aadhaar._id}`,
          patchPayload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }

      // ✅ Update local state
      setFormData((prev) => ({
        ...prev,
        [side]: uploadedUrl,
      }));

      // Refresh driver data
      if (onUpdated) onUpdated();
    } catch (err) {
      console.error("Upload error:", err);
      alert("Failed to upload Aadhaar image.");
    } finally {
      setUploadingSide(null);
    }
  };

  const handleSaveField = async (field) => {
    if (field === "aadhaarNumber") {
      const trimmed = (formData.aadhaarNumber || "").trim();
      const currentTrimmed = (aadhaar?.aadhaarNumber || "").trim();
      if (trimmed && trimmed !== currentTrimmed) {
        try {
          const alreadyUsed = await isAadhaarNumberAlreadyUsed(trimmed);
          if (alreadyUsed) {
            setAadhaarNumberError(AADHAAR_DUPLICATE_MESSAGE);
            return;
          }
        } catch (checkErr) {
          console.error("Aadhaar duplicate check error:", checkErr);
          setAadhaarNumberError("Failed to validate aadhaar number");
          return;
        }
      }
      setAadhaarNumberError("");
    }

    setIsSaving(true);
    try {
      const payload = {};
      if (field === "status") {
        payload.status = formData.status;
        if (formData.status === "Rejected" || formData.status === "Revisit") {
          payload.statusReason = formData.statusReason;
        }
      } else {
        payload[field] = formData[field];
      }

      // If aadhaar record doesn't exist, create it
      if (!aadhaar || !aadhaar._id) {
        const createPayload =
          field === "aadhaarNumber"
            ? {
                ownerId: driverId,
                aadhaarNumber: (formData.aadhaarNumber || "").trim(),
                aadhaarfront: formData.aadhaarfront || "",
                aadhaarback: formData.aadhaarback || "",
              }
            : {
                ownerId: driverId,
                aadhaarNumber: formData.aadhaarNumber || "",
                nameToMatch: formData.fullName || "",
                consent: "Y",
                consentText:
                  "I hereby declare my consent agreement for fetching my information via ZOOP API",
                taskId: "08b01aa8-9487-4e6d-a0f0-c796839d6b77",
                aadhaarfront: formData.aadhaarfront || "",
                aadhaarback: formData.aadhaarback || "",
                ...payload,
              };

        const createResponse = await fetch(API.ADMIN.ADMIN_VERIFY_AADHAAR, {
          method: "POST",
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(createPayload),
        });

        let createResult = null;
        try {
          createResult = await createResponse.json();
        } catch (parseErr) {
          createResult = null;
        }

        if (
          field === "aadhaarNumber" &&
          (!createResponse.ok ||
            createResult?.success === false ||
            isDuplicateAadhaarMessage(
              createResult?.message || createResult?.error
            ))
        ) {
          if (
            isDuplicateAadhaarMessage(
              createResult?.message || createResult?.error
            )
          ) {
            setAadhaarNumberError(AADHAAR_DUPLICATE_MESSAGE);
          } else {
            setAadhaarNumberError(
              createResult?.message ||
                createResult?.error ||
                "Failed to update field"
            );
          }
          return;
        }

        if (!createResponse.ok) {
          throw new Error(
            createResult?.message || createResult?.error || "Failed to update field"
          );
        }
      } else {
        await axios.patch(
          `${API.ADMIN.AADHAAR_DETAILS}/${aadhaar._id}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }

      setEditField("");
      if (field === "aadhaarNumber") {
        setAadhaarNumberError("");
      }
      if (onUpdated) onUpdated();
    } catch (err) {
      console.error("Update error:", err);
      const apiMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message;
      if (field === "aadhaarNumber") {
        if (isDuplicateAadhaarMessage(apiMessage)) {
          setAadhaarNumberError(AADHAAR_DUPLICATE_MESSAGE);
        } else {
          setAadhaarNumberError(apiMessage || "Failed to update field");
        }
      } else {
        alert("Failed to update field");
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="p-6">
      <div className="flex items-center gap-4 mb-6">
        <FaIdCard className="text-green-600 text-2xl" />
        <h2 className="text-2xl font-semibold text-gray-800">
          Aadhaar Details
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Full Name */}
        <div>
          <strong>Full Name:</strong>{" "}
          {editField === "fullName" ? (
            <span className="flex gap-2 items-center">
              <input
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                className="border px-2 py-1 rounded text-sm"
              />
              <FaCheck
                className="text-green-600 cursor-pointer"
                onClick={() => handleSaveField("fullName")}
              />
              <FaTimes
                className="text-red-600 cursor-pointer"
                onClick={() => {
                  setFormData((p) => ({ ...p, fullName: aadhaar.fullName }));
                  setEditField("");
                }}
              />
            </span>
          ) : (
            <span className="flex items-center gap-2">
              {formData.fullName || (showIfEmpty && detailsEditable ? "Click to add" : "N/A")}
              {detailsEditable && (
                <FaEdit
                  className="text-blue-600 cursor-pointer"
                  onClick={() => setEditField("fullName")}
                />
              )}
            </span>
          )}
        </div>

        {/* Aadhaar Number */}
        <div>
          <strong>Aadhaar Number:</strong>{" "}
          {editField === "aadhaarNumber" ? (
            <span className="inline-flex flex-col">
              <span className="flex gap-2 items-center">
                <input
                  name="aadhaarNumber"
                  value={formData.aadhaarNumber}
                  onChange={handleChange}
                  className="border px-2 py-1 rounded text-sm"
                />
                {isSaving ? (
                  <FaSpinner className="animate-spin text-gray-500" />
                ) : (
                  <>
                    <FaCheck
                      className="text-green-600 cursor-pointer"
                      onClick={() => handleSaveField("aadhaarNumber")}
                    />
                    <FaTimes
                      className="text-red-600 cursor-pointer"
                      onClick={() => {
                        setFormData((p) => ({
                          ...p,
                          aadhaarNumber: aadhaar?.aadhaarNumber || "",
                        }));
                        setAadhaarNumberError("");
                        setEditField("");
                      }}
                    />
                  </>
                )}
              </span>
              {aadhaarNumberError && (
                <p className="text-red-500 text-sm mt-1">{aadhaarNumberError}</p>
              )}
            </span>
          ) : (
            <span className="flex items-center gap-2">
              {formData.aadhaarNumber || (showIfEmpty && detailsEditable ? "Click to add" : "N/A")}
              {detailsEditable && (
                <FaEdit
                  className="text-blue-600 cursor-pointer"
                  onClick={() => {
                    setAadhaarNumberError("");
                    setEditField("aadhaarNumber");
                  }}
                />
              )}
            </span>
          )}
        </div>

        {/* Aadhaar Images */}
        <div className="md:col-span-2">
          <strong className="block mb-2">Aadhaar Images:</strong>
          <div className="flex gap-8 flex-wrap">
            {["aadhaarfront", "aadhaarback"].map((side) => (
              <div key={side} className="flex flex-col items-start">
                <span className="font-medium mb-1 capitalize">
                  {side === "aadhaarfront" ? "Front" : "Back"}
                </span>
                <DocumentImage
                  imageUrl={formData[side]}
                  imageName={`Aadhaar ${
                    side === "aadhaarfront" ? "Front" : "Back"
                  }`}
                  alt={`Aadhaar ${side}`}
                >
                  {detailsEditable && (
                    <label className="mt-2 cursor-pointer flex gap-2 items-center text-sm text-blue-600">
                      {uploadingSide === side ? (
                        <FaSpinner className="animate-spin" />
                      ) : (
                        <>
                          <FaUpload />
                          {formData[side] ? "Change" : "Upload"}
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleFileChange(e, side)}
                        disabled={uploadingSide}
                      />
                    </label>
                  )}
                </DocumentImage>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Status Editor */}
      <DocumentStatusEditor
        statusForm={formData}
        setStatusForm={setFormData}
        isEditing={editField === "status"}
        onEdit={() => setEditField("status")}
        onCancel={() => {
          setFormData((prev) => ({
            ...prev,
            status: aadhaar.status,
            statusReason: aadhaar.statusReason,
          }));
          setEditField("");
        }}
        onSave={() => handleSaveField("status")}
        editable={statusEditable}
        userRole={currentUserRole}
      />
    </section>
  );
};

export default AadhaarDetails;
